import React from "react";
import styled, { keyframes } from "styled-components";
import { UtensilsCrossed, BellRing, RefreshCw } from "lucide-react";

import { useNotification } from "../contexts/notification";
import { useLanguage } from "../contexts/language";

/* Shown between "pay" and the Xendit page, because after paying is too late to learn the order is
 * self pick-up. Non-dismissible and buttonless on purpose: there is nothing to decide, only
 * something to read, and a button would just be clicked past.
 *
 * Three seconds is the whole budget — long enough to read one instruction, short enough that the
 * guest never wonders whether the payment is stuck. The countdown says so out loud.
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const Card = styled.div`
  animation: ${fadeInScale} 220ms ease-out;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
  max-width: 420px;
  padding: 1.6rem 1.4rem 1.3rem;
  text-align: center;
  width: 100%;
`;

const IconRing = styled.div`
  align-items: center;
  background: #fff4e0;
  border-radius: 999px;
  color: #a15c00;
  display: inline-flex;
  height: 56px;
  justify-content: center;
  margin-bottom: 0.85rem;
  width: 56px;
`;

const Title = styled.h2`
  color: #1f2430;
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.35;
  margin: 0 0 0.5rem;
`;

const Where = styled.p`
  color: #4a4f63;
  font-size: 0.92rem;
  line-height: 1.55;
  margin: 0 0 0.9rem;
`;

const NotifyRow = styled.div`
  align-items: flex-start;
  background: #f4f6fa;
  border-radius: 10px;
  color: #2f4a70;
  display: flex;
  font-size: 0.86rem;
  font-weight: 600;
  gap: 0.5rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  padding: 0.65rem 0.75rem;
  text-align: left;
`;

const Redirect = styled.p`
  color: #1f2430;
  font-size: 0.92rem;
  font-weight: 800;
  margin: 0;
`;

const Track = styled.div`
  background: #eceef3;
  border-radius: 999px;
  height: 5px;
  margin-top: 0.7rem;
  overflow: hidden;
`;

const Fill = styled.div`
  background: #a15c00;
  height: 100%;
  transition: width 1s linear;
  width: ${(props) => props.$percent}%;
`;

const SECONDS = 3;

const PickupNoticeModal = (props) => {
  const { t } = useLanguage();
  const { notificationState } = useNotification();
  const [remaining, setRemaining] = React.useState(SECONDS);

  const onDone = props.onDone;

  React.useEffect(() => {
    const id = setInterval(() => setRemaining((prev) => prev - 1), 1000);
    return () => clearInterval(id);
  }, []);

  /* Fires from the countdown reaching zero rather than from a setTimeout, so what the guest reads
   * and what actually happens cannot drift apart if the tab is throttled.
   */
  React.useEffect(() => {
    if (remaining > 0) return;
    onDone();
  }, [remaining, onDone]);

  const willBeNotified = notificationState === "subscribed";
  const copy = t.order.pickupNotice;
  const shown = Math.max(remaining, 0);

  return (
    <Overlay>
      <Card role="dialog" aria-modal="true" aria-live="polite">
        <IconRing>
          <UtensilsCrossed size={26} />
        </IconRing>
        <Title>{copy.title}</Title>
        <Where>{copy.where}</Where>

        <NotifyRow>
          {willBeNotified ? (
            <BellRing size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          ) : (
            <RefreshCw size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          )}
          <span>{willBeNotified ? copy.notifyOn : copy.notifyOff}</span>
        </NotifyRow>

        <Redirect>{copy.redirect.replace("%{seconds}", `${shown}s`)}</Redirect>
        <Track>
          <Fill $percent={(shown / SECONDS) * 100} />
        </Track>
      </Card>
    </Overlay>
  );
};

export default PickupNoticeModal;
